import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI, applicationsAPI, escrowAPI, favoriteWorkersAPI } from '../api/api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Briefcase, 
  MapPin, 
  CurrencyDollar, 
  Clock,
  Users,
  Plus,
  Eye,
  CheckCircle,
  XCircle,
  Warning,
  CaretDown,
  CaretUp,
  CalendarBlank,
  ChatCircle,
  UserCheck,
  UserMinus,
  SpinnerGap,
  MagnifyingGlass,
  Funnel,
  ShieldCheck,
  X,
  Trash,
  ArrowRight,
  Star
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { BreadcrumbsInline } from '../components/Breadcrumbs';

const MyJobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedJob, setExpandedJob] = useState(null);
  const [applications, setApplications] = useState({});
  const [loadingApps, setLoadingApps] = useState({});
  const [processingApp, setProcessingApp] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [favorited, setFavorited] = useState({});
  
  // Escrow modal state
  const [showEscrowModal, setShowEscrowModal] = useState(false);
  const [escrowData, setEscrowData] = useState({
    jobId: null,
    jobTitle: '',
    workerId: null,
    workerName: '',
    amount: '',
    description: '',
    milestones: []
  });
  const [creatingEscrow, setCreatingEscrow] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    
    if (!token) {
      navigate('/login?type=contractor');
      return;
    }
    
    if (userType !== 'contractor') {
      toast({
        title: 'Access Denied',
        description: 'Only contractors can view this page',
        variant: 'destructive'
      });
      navigate('/');
      return;
    }
    
    fetchJobs();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [navigate, statusFilter]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await jobsAPI.getMyJobs(params);
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load your jobs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async (jobId) => {
    if (applications[jobId]) return;
    
    setLoadingApps(prev => ({ ...prev, [jobId]: true }));
    try {
      const response = await applicationsAPI.getJobApplications(jobId);
      setApplications(prev => ({ ...prev, [jobId]: response.data.applications || [] }));
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load applications',
        variant: 'destructive'
      });
    } finally {
      setLoadingApps(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const handleExpandJob = (jobId) => {
    if (expandedJob === jobId) {
      setExpandedJob(null);
    } else {
      setExpandedJob(jobId);
      fetchApplications(jobId);
    }
  };

  const handleUpdateApplicationStatus = async (applicationId, jobId, newStatus, workerInfo = null) => {
    setProcessingApp(applicationId);
    try {
      await applicationsAPI.updateStatus(applicationId, newStatus);
      
      setApplications(prev => ({
        ...prev,
        [jobId]: prev[jobId].map(app => 
          app._id === applicationId ? { ...app, status: newStatus } : app
        )
      }));
      
      toast({
        title: newStatus === 'accepted' ? 'Worker Accepted!' : 'Application Rejected',
        description: newStatus === 'accepted' 
          ? 'The worker has been notified.'
          : 'The application has been rejected.',
      });
      
      if (newStatus === 'accepted' && workerInfo) {
        const job = jobs.find(j => j._id === jobId);
        setEscrowData({
          jobId: jobId,
          jobTitle: job?.title || 'Job',
          workerId: workerInfo.worker_id,
          workerName: workerInfo.worker?.name || 'Worker',
          amount: job?.budget_max || job?.budget_min || '',
          description: `Payment for ${job?.title || 'job'}`,
          milestones: []
        });
        setShowEscrowModal(true);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update application status',
        variant: 'destructive'
      });
    } finally {
      setProcessingApp(null);
    }
  };
  
  const handleCreateEscrow = async () => {
    if (!escrowData.amount || parseFloat(escrowData.amount) <= 0) {
      toast({
        title: 'Error',
        description: 'Please enter a valid amount',
        variant: 'destructive'
      });
      return;
    }
    
    setCreatingEscrow(true);
    try {
      const response = await escrowAPI.create({
        job_id: escrowData.jobId,
        worker_id: escrowData.workerId,
        amount: parseFloat(escrowData.amount),
        description: escrowData.description,
        milestones: escrowData.milestones.filter(m => m.name && m.amount > 0)
      });
      
      if (response.data.success) {
        toast({
          title: 'Escrow Created!',
          description: 'Redirecting to fund...'
        });
        setShowEscrowModal(false);
        navigate(`/escrow/${response.data.escrow._id}`);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create escrow',
        variant: 'destructive'
      });
    } finally {
      setCreatingEscrow(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-400';
      case 'closed': return 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-400 border-neutral-400 dark:border-neutral-600';
      case 'filled': return 'bg-[#2B4C6F]/10 text-[#2B4C6F] border-[#2B4C6F]';
      default: return 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-400 border-neutral-400 dark:border-neutral-600';
    }
  };

  const getAppStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-400';
      case 'pending': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-400';
      case 'reviewed': return 'bg-[#2B4C6F]/10 text-[#2B4C6F] border-[#2B4C6F]';
      case 'rejected': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-400';
      default: return 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-400 border-neutral-400 dark:border-neutral-600';
    }
  };

  const toggleFavorite = async (workerId, workerName) => {
    const isFav = favorited[workerId];
    try {
      if (isFav) {
        await favoriteWorkersAPI.remove(workerId);
        setFavorited(p => ({ ...p, [workerId]: false }));
        toast({ title: `${workerName} removed from favorites` });
      } else {
        await favoriteWorkersAPI.add(workerId);
        setFavorited(p => ({ ...p, [workerId]: true }));
        toast({ title: `${workerName} added to favorites!` });
      }
    } catch { toast({ title: 'Error updating favorites', variant: 'destructive' }); }
  };

  const stats = {
    total: jobs.length,
    open: jobs.filter(j => j.status === 'open').length,
    filled: jobs.filter(j => j.status === 'filled').length,
    totalApplications: jobs.reduce((sum, j) => sum + (j.application_count || 0), 0)
  };

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 pb-24 transition-colors duration-300" data-testid="my-jobs-page">
      {/* Header */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black dark:border-[#2B4C6F]">
        <div className="max-w-5xl mx-auto px-4">
          <BreadcrumbsInline 
            items={[
              { label: 'Dashboard', href: '/contractor-dashboard' },
              { label: 'My Jobs' }
            ]}
            className="mb-2"
          />
          <h1 className="text-3xl md:text-4xl font-black">My Jobs</h1>
          <p className="text-blue-100 text-lg mt-1">Manage your listings and applicants</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button 
            onClick={() => navigate('/post-job')}
            className="flex items-center gap-2 h-14 px-6 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            data-testid="post-new-job-btn"
          >
            <Plus size={24} weight="bold" />
            Post New Job
          </button>
          <button 
            onClick={() => navigate('/escrow')}
            className="flex items-center gap-2 h-14 px-6 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white font-bold text-lg rounded-xl border-2 border-black dark:border-neutral-700 shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F] hover:shadow-[4px_4px_0px_#000] dark:hover:shadow-[4px_4px_0px_#2B4C6F] active:translate-y-1 active:shadow-none transition-all"
          >
            <ShieldCheck size={24} weight="bold" />
            Escrow
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-4 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <Briefcase size={28} weight="bold" className="text-[#2B4C6F] mb-2" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Total Jobs</p>
            <p className="text-2xl font-black text-neutral-900 dark:text-white">{stats.total}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-4 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <CheckCircle size={28} weight="bold" className="text-emerald-600 mb-2" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Open</p>
            <p className="text-2xl font-black text-emerald-600">{stats.open}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-4 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <UserCheck size={28} weight="bold" className="text-[#2B4C6F] mb-2" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Filled</p>
            <p className="text-2xl font-black text-[#2B4C6F]">{stats.filled}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-4 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
            <Users size={28} weight="bold" className="text-purple-600 dark:text-purple-400 mb-2" />
            <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">Applications</p>
            <p className="text-2xl font-black text-purple-600 dark:text-purple-400">{stats.totalApplications}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-3 mb-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
          <div className="flex flex-wrap items-center gap-2">
            <Funnel size={20} weight="bold" className="text-neutral-500 dark:text-neutral-400" />
            {['all', 'open', 'closed', 'filled'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 font-bold rounded-lg transition-all ${
                  statusFilter === status 
                    ? 'bg-[#2B4C6F] text-white' 
                    : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
                }`}
                data-testid={`filter-${status}`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-8 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] text-center">
            <Briefcase size={64} weight="light" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-xl font-black text-neutral-900 dark:text-white mb-2">No Jobs Posted Yet</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-6">Post your first job to start finding workers</p>
            <button 
              onClick={() => navigate('/post-job')}
              className="h-14 px-6 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            >
              <Plus size={20} weight="bold" className="inline mr-2" />
              Post Your First Job
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <div 
                key={job._id} 
                className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] overflow-hidden"
                data-testid={`job-card-${job._id}`}
              >
                {/* Job Header */}
                <div 
                  className="p-5 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-all"
                  onClick={() => handleExpandJob(job._id)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-black text-neutral-900 dark:text-white">{job.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-bold border-2 ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-neutral-600 dark:text-neutral-400">
                        <span className="flex items-center gap-1">
                          <MapPin size={18} weight="bold" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <CurrencyDollar size={18} weight="bold" />
                          ${job.budget_min?.toLocaleString()} - ${job.budget_max?.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users size={18} weight="bold" />
                          {job.application_count || 0} applications
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/job/${job._id}`);
                        }}
                        className="p-2 text-neutral-500 dark:text-neutral-400 hover:text-[#2B4C6F] transition-all"
                      >
                        <Eye size={24} weight="bold" />
                      </button>
                      {expandedJob === job._id ? (
                        <CaretUp size={24} weight="bold" className="text-neutral-500 dark:text-neutral-400" />
                      ) : (
                        <CaretDown size={24} weight="bold" className="text-neutral-500 dark:text-neutral-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Applications */}
                {expandedJob === job._id && (
                  <div className="border-t-2 border-neutral-200 dark:border-neutral-700 p-5 bg-neutral-50 dark:bg-neutral-900/50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-black text-neutral-900 dark:text-white">Applications ({applications[job._id]?.length || 0})</h4>
                      {(applications[job._id]?.length || 0) >= 2 && (
                        <button onClick={() => navigate(`/bid-comparison/${job._id}`)}
                          className="h-8 px-3 bg-[#2B4C6F] hover:bg-[#203A55] text-white text-xs font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all flex items-center gap-1"
                          data-testid={`compare-bids-${job._id}`}>
                          Compare Side-by-Side
                        </button>
                      )}
                    </div>
                    
                    {loadingApps[job._id] ? (
                      <div className="flex justify-center py-4">
                        <SpinnerGap size={32} weight="bold" className="animate-spin text-[#2B4C6F]" />
                      </div>
                    ) : !applications[job._id] || applications[job._id].length === 0 ? (
                      <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No applications yet</p>
                    ) : (
                      <div className="space-y-3">
                        {applications[job._id].map((app) => (
                          <div 
                            key={app._id}
                            className="bg-white dark:bg-neutral-800 border-2 border-neutral-200 dark:border-neutral-700 rounded-xl p-4"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-bold text-neutral-900 dark:text-white text-lg">{app.worker?.name || 'Worker'}</p>
                                <p className="text-neutral-600 dark:text-neutral-400">Rate: {app.proposed_rate}</p>
                                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{app.cover_letter?.substring(0, 100)}...</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleFavorite(app.worker_id, app.worker?.name || 'Worker')}
                                  className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-colors ${
                                    favorited[app.worker_id]
                                      ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-400 dark:border-amber-600'
                                      : 'bg-neutral-100 dark:bg-neutral-700 border-neutral-300 dark:border-neutral-600 hover:bg-amber-50 dark:hover:bg-amber-900/20'
                                  }`}
                                  title={favorited[app.worker_id] ? 'Remove from favorites' : 'Add to favorites'}
                                  data-testid={`star-worker-${app.worker_id}`}
                                >
                                  <Star size={16} weight={favorited[app.worker_id] ? 'fill' : 'bold'} className={favorited[app.worker_id] ? 'text-amber-500' : 'text-neutral-400'} />
                                </button>
                                <span className={`px-3 py-1 rounded-full text-sm font-bold border-2 ${getAppStatusColor(app.status)}`}>
                                  {app.status}
                                </span>
                              </div>
                            </div>
                            
                            {app.status === 'pending' && (
                              <div className="flex gap-2 mt-4">
                                <button
                                  onClick={() => handleUpdateApplicationStatus(app._id, job._id, 'accepted', app)}
                                  disabled={processingApp === app._id}
                                  className="flex-1 h-12 flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold rounded-lg border-2 border-black hover:bg-emerald-700 disabled:opacity-50 transition-all"
                                >
                                  {processingApp === app._id ? (
                                    <SpinnerGap size={20} className="animate-spin" />
                                  ) : (
                                    <><UserCheck size={20} weight="bold" /> Accept</>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleUpdateApplicationStatus(app._id, job._id, 'rejected')}
                                  disabled={processingApp === app._id}
                                  className="flex-1 h-12 flex items-center justify-center gap-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 font-bold rounded-lg border-2 border-red-300 dark:border-red-700 hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50 transition-all"
                                >
                                  <UserMinus size={20} weight="bold" /> Reject
                                </button>
                                <button
                                  onClick={() => navigate(`/messages?user=${app.worker_id}`)}
                                  className="h-12 px-4 flex items-center justify-center bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-bold rounded-lg border-2 border-neutral-300 dark:border-neutral-600 hover:bg-neutral-200 dark:hover:bg-neutral-600 transition-all"
                                >
                                  <ChatCircle size={20} weight="bold" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Escrow Modal */}
      {showEscrowModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#2B4C6F] max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-black text-neutral-900 dark:text-white">Create Escrow</h2>
                <button 
                  onClick={() => setShowEscrowModal(false)}
                  className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg"
                >
                  <X size={24} weight="bold" className="text-neutral-600 dark:text-neutral-400" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-neutral-50 dark:bg-neutral-700 border-2 border-neutral-200 dark:border-neutral-600 rounded-xl p-4">
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">Job</p>
                  <p className="font-bold text-neutral-900 dark:text-white">{escrowData.jobTitle}</p>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">Worker</p>
                  <p className="font-bold text-neutral-900 dark:text-white">{escrowData.workerName}</p>
                </div>
                
                <div>
                  <Label className="font-bold mb-2 block text-neutral-900 dark:text-white">Amount *</Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400 font-bold">$</span>
                    <Input
                      type="number"
                      value={escrowData.amount}
                      onChange={(e) => setEscrowData({...escrowData, amount: e.target.value})}
                      className="h-14 text-lg border-2 border-black dark:border-neutral-600 rounded-xl pl-8 dark:bg-neutral-700 dark:text-white"
                      placeholder="5,000"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="font-bold mb-2 block text-neutral-900 dark:text-white">Description</Label>
                  <Textarea
                    value={escrowData.description}
                    onChange={(e) => setEscrowData({...escrowData, description: e.target.value})}
                    className="border-2 border-black dark:border-neutral-600 rounded-xl dark:bg-neutral-700 dark:text-white"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowEscrowModal(false)}
                  className="flex-1 h-12 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-bold rounded-xl border-2 border-neutral-300 dark:border-neutral-600"
                >
                  Skip
                </button>
                <button
                  onClick={handleCreateEscrow}
                  disabled={creatingEscrow}
                  className="flex-1 h-14 flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] disabled:opacity-50"
                >
                  {creatingEscrow ? (
                    <SpinnerGap size={20} className="animate-spin" />
                  ) : (
                    <><ShieldCheck size={20} weight="bold" /> Create Escrow</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyJobs;
